import React from 'react';
import { Space, Button, Input, Dropdown, MenuProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import FilterIcon from '../../../../../assets/icons/view-attribute-table-filter.svg';
import ClearIcon from '../../../../../assets/icons/view-attribute-table-clear-selection.svg';
import ReverseIcon from '../../../../../assets/icons/view-attribute-table-inverse-selection.svg';
import EditIcon from '../../../../../assets/icons/view-attribute-table-edit.svg';
import DownloadIcon from '../../../../../assets/icons/view-attribute-table-download.svg';
import SaveIcon from '../../../../../assets/icons/view-attribute-table-save.svg';
import ConditionalRender from '../../../../../shared/components/ConditionalRender';

interface TableHeaderProps {
  isQueryTool?: boolean;
  viewActive: boolean;
  reverseActive: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isEditing: boolean;
  hasSelection: boolean;
  onToggleViewSelection: () => void;
  onClearSelection: () => void;
  onReverseSelection: () => void;
  onToggleEdit: () => void;
  onShowAddColumn: () => void;
  onShowDownload: () => void;
  saveMenu: MenuProps['items'];
  // US-101362 AC-05: edit-mode and Save are Editor/Admin only. Viewers don't
  // get the pencil or the save-as-layer action.
  canEdit?: boolean;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  isQueryTool,
  viewActive,
  reverseActive,
  searchTerm,
  setSearchTerm,
  isEditing,
  hasSelection,
  onToggleViewSelection,
  onClearSelection,
  onReverseSelection,
  onToggleEdit,
  onShowAddColumn,
  onShowDownload,
  saveMenu,
  canEdit = true,
}) => {
  return (
    <div className="headerContainer">
      <div className="headerContent">
        {/* US-103191/93/97: the Query result table shares the attribute table's
            Filter (display-selection) / Clear / Interchange toolbar. */}
        <Space>
          <Button
            icon={<FilterIcon />}
            onClick={onToggleViewSelection}
            shape="circle"
            title="Display Selection"
            className={viewActive ? 'vat-header__toggle vat-header__toggle--active' : 'vat-header__toggle'}
          />
          <Button icon={<ClearIcon />} onClick={onClearSelection} shape="circle" title="Clear Selection" />
          <Button
            icon={<ReverseIcon />}
            onClick={onReverseSelection}
            shape="circle"
            title="Reverse Selection"
            className={reverseActive ? 'vat-header__toggle vat-header__toggle--active' : 'vat-header__toggle'}
          />
        </Space>

        <div className="actionsSection">
          <div className="actionsRow">
            <Input placeholder="Search Layer..." className="vat-header__search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {/* Edit is hidden when the table is opened from the Query/Proximity
                tool (isQueryTool). Kept in code; only the render is gated. */}
            <ConditionalRender visible={canEdit && !isQueryTool}>
              <Button
                icon={<EditIcon />}
                shape="circle"
                onClick={onToggleEdit}
                className={isEditing ? 'vat-header__toggle vat-header__toggle--active' : 'vat-header__toggle'}
              />
            </ConditionalRender>
            {/* US-101362 AC-07/08: Download is always enabled. With a
                selection it exports only those features; without one it
                exports the full dataset. */}
            <Button icon={<DownloadIcon />} shape="circle" onClick={onShowDownload} />

            <ConditionalRender visible={canEdit}>
              <Dropdown
                disabled={!hasSelection && !isQueryTool}
                menu={{ items: saveMenu }}
                placement="bottomRight"
                trigger={['click']}
                overlayClassName="vat-save-dropdown"
              >
                <Button icon={<SaveIcon />} shape="circle" disabled={!hasSelection && !isQueryTool} />
              </Dropdown>
            </ConditionalRender>
          </div>

          <ConditionalRender visible={isEditing && canEdit}>
            <Button icon={<PlusOutlined />} shape="circle" onClick={onShowAddColumn} title="Add New Field" />
          </ConditionalRender>
        </div>
      </div>
    </div>
  );
};

export default TableHeader;
